import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../api/client";

/** Written (open-ended) answers must meet this word count before submit (frontend only). */
const MIN_OPEN_ENDED_WORDS = 50;

function countWords(text: string): number {
  const t = text.trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

type LessonData = {
  id: string;
  title: string;
  unlocked: boolean;
  completed: boolean;
  passingScore?: number;
  submissionStatus?: "none" | "pending" | "graded";
};

type QuizQuestion = {
  id: string;
  questionType: "multipleChoice" | "openEnded";
  question: string;
  options: string[];
};

type QuizData = {
  quizId: string;
  title: string;
  questions: QuizQuestion[];
};

type SubmissionAnswer = {
  questionId: string;
  questionType: string;
  questionText: string;
  options: string[];
  selectedOption: string;
  textAnswer: string;
  correct: boolean | null;
  adminNote: string;
  explanation: string;
};

type SubmissionData = {
  submissionId: string;
  status: "pending" | "graded";
  submittedAt: string;
  passed: boolean | null;
  score: number | null;
  adminFeedback: string;
  gradedAt: string | null;
  answers: SubmissionAnswer[] | null;
};

export function LessonQuizPage() {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>();
  const [lesson, setLesson] = useState<LessonData | null>(null);
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshLesson = () => {
    if (!slug || !lessonId) return;
    api.get(`/courses/${slug}/lessons/${lessonId}`).then((res) => {
      if (res.data.success) setLesson(res.data.data);
    });
  };

  useEffect(() => {
    if (!slug || !lessonId) return;
    Promise.all([
      api.get(`/courses/${slug}/lessons/${lessonId}`),
      api.get(`/courses/${slug}/lessons/${lessonId}/quiz`),
    ])
      .then(([lessonRes, quizRes]) => {
        if (lessonRes.data.success) setLesson(lessonRes.data.data);
        if (quizRes.data.success) setQuiz(quizRes.data.data);
      })
      .catch(() => setError("Could not load lesson quiz."))
      .finally(() => setLoading(false));
  }, [slug, lessonId]);

  if (loading) {
    return (
      <div className="min-h-full bg-stone/40">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-40 bg-gray-200 rounded" />
            <div className="h-8 w-3/4 bg-gray-200 rounded" />
            <div className="h-40 bg-gray-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !lesson || !quiz) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-red-200 bg-white p-10 text-center shadow-soft max-w-md">
          <p className="text-red-600 font-medium">{error || "Quiz not found."}</p>
          <Link to={`/dashboard/courses/${slug}/lessons/${lessonId}`} className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to lesson
          </Link>
        </div>
      </div>
    );
  }

  if (!lesson.unlocked) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-amber-200 bg-white p-10 text-center shadow-soft max-w-md">
          <p className="text-amber-800 font-medium">Complete the previous lesson first.</p>
          <Link to={`/dashboard/courses/${slug}`} className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to course
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to={`/dashboard/courses/${slug}/lessons/${lessonId}`}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to lesson
          </Link>
          <h1 className="mt-6 font-serif text-2xl md:text-3xl font-semibold tracking-tight">
            {lesson.title} - Quiz
          </h1>
          <p className="mt-2 text-slate-400 text-sm">
            This page is dedicated to testing. Video playback is not shown here.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <LessonQuizBlock
          slug={slug!}
          lessonId={lessonId!}
          quiz={quiz}
          passingScore={lesson.passingScore ?? 80}
          submissionStatus={lesson.submissionStatus ?? "none"}
          lessonCompleted={lesson.completed}
          onRefresh={refreshLesson}
        />
      </div>
    </div>
  );
}

function LessonQuizBlock({
  slug,
  lessonId,
  quiz,
  passingScore,
  submissionStatus,
  lessonCompleted,
  onRefresh,
}: {
  slug: string;
  lessonId: string;
  quiz: QuizData;
  passingScore: number;
  submissionStatus: string;
  lessonCompleted: boolean;
  onRefresh: () => void;
}) {
  const [mcAnswers, setMcAnswers] = useState<Record<string, string>>({});
  const [openAnswers, setOpenAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(submissionStatus !== "none");
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [subLoading, setSubLoading] = useState(false);

  const beginRetest = () => {
    setSubmitted(false);
    setSubmission(null);
    setMcAnswers({});
    setOpenAnswers({});
  };

  useEffect(() => {
    if (submissionStatus !== "none") {
      setSubLoading(true);
      api
        .get(`/courses/${slug}/lessons/${lessonId}/quiz/submission`)
        .then((res) => {
          if (res.data.success && res.data.data) {
            setSubmission(res.data.data);
            setSubmitted(true);
          }
        })
        .catch(() => {})
        .finally(() => setSubLoading(false));
    }
  }, [slug, lessonId, submissionStatus]);

  if (lessonCompleted && submission?.passed) {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50/80 p-6 md:p-8 shadow-soft">
        <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">Lesson quiz</h3>
        <p className="mt-2 text-sm text-green-800">
          You passed this lesson&apos;s quiz (course requirement: {passingScore}% or higher). This lesson is complete.
        </p>
        <Link
          to={`/dashboard/courses/${slug}`}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-ink-950 px-6 py-3.5 text-white font-semibold hover:bg-ink-800 transition-colors"
        >
          Back to course
        </Link>
      </div>
    );
  }

  if (subLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-soft text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 bg-gray-200 rounded mx-auto" />
          <div className="h-4 w-64 bg-gray-100 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (submitted && submission?.status === "pending") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-6 md:p-8 shadow-soft">
        <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">Quiz submitted</h3>
        <p className="mt-2 text-amber-800">
          Your answers are waiting for instructor review. You&apos;ll see the result here once graded.
        </p>
        <button
          type="button"
          onClick={onRefresh}
          className="mt-4 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-50 transition-colors"
        >
          Check for updates
        </button>
      </div>
    );
  }

  if (submitted && submission?.status === "graded") {
    const passed = !!submission.passed;
    return (
      <div className={`rounded-2xl border ${passed ? "border-green-200 bg-green-50/80" : "border-red-200 bg-red-50/80"} p-6 md:p-8 shadow-soft`}>
        <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">
          Quiz result: {passed ? "Passed" : "Not passed"}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Score: {submission.score ?? "—"}% (required: {passingScore}%)
        </p>
        {!passed && (
          <button
            type="button"
            onClick={beginRetest}
            className="mt-4 rounded-lg bg-gold-400 px-5 py-2.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors"
          >
            Retest quiz
          </button>
        )}
      </div>
    );
  }

  const mcQuestions = quiz.questions.filter((q) => q.questionType !== "openEnded");
  const oeQuestions = quiz.questions.filter((q) => q.questionType === "openEnded");

  const handleSubmit = () => {
    for (const q of oeQuestions) {
      if (countWords(openAnswers[q.id] || "") < MIN_OPEN_ENDED_WORDS) {
        alert(`Please write at least ${MIN_OPEN_ENDED_WORDS} words for each written answer before submitting.`);
        return;
      }
    }
    const answers = quiz.questions.map((q) => ({
      questionId: q.id,
      questionType: q.questionType || "multipleChoice",
      selectedOption: q.questionType === "openEnded" ? "" : mcAnswers[q.id] || "",
      textAnswer: q.questionType === "openEnded" ? openAnswers[q.id] || "" : "",
    }));
    setSubmitting(true);
    api
      .post(`/courses/${slug}/lessons/${lessonId}/quiz/submit`, { answers })
      .then((res) => {
        if (res.data.success) {
          setSubmitted(true);
          setSubmission({
            submissionId: res.data.data.submissionId,
            status: "pending",
            submittedAt: new Date().toISOString(),
            passed: null,
            score: null,
            adminFeedback: "",
            gradedAt: null,
            answers: null,
          });
          onRefresh();
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Failed to submit.";
        alert(msg);
      })
      .finally(() => setSubmitting(false));
  };

  const allMcAnswered = mcQuestions.every((q) => mcAnswers[q.id]);
  const allOeAnswered = oeQuestions.every((q) => countWords(openAnswers[q.id] || "") >= MIN_OPEN_ENDED_WORDS);
  const canSubmit = allMcAnswered && allOeAnswered;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-soft">
      <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">
        {quiz.title || "Lesson Quiz"}
      </h3>
      <p className="mt-2 text-sm text-gray-500">
        Passing score: <span className="font-semibold text-ink-950">{passingScore}%</span>. Answer all questions before submitting.
        {oeQuestions.length > 0 && (
          <>
            {" "}
            Written answers must be at least <span className="font-semibold text-ink-950">{MIN_OPEN_ENDED_WORDS} words</span>{" "}
            each.
          </>
        )}
      </p>

      {quiz.questions.map((q, qIdx) => {
        const openWordCount = q.questionType === "openEnded" ? countWords(openAnswers[q.id] || "") : 0;
        const openAnswerTooShort =
          q.questionType === "openEnded" && openWordCount > 0 && openWordCount < MIN_OPEN_ENDED_WORDS;
        return (
          <div key={q.id} className="mt-6">
            <p className="font-medium text-ink-950">
              <span className="text-gold-500 mr-1">Q{qIdx + 1}.</span>
              {q.question}
            </p>
            {q.questionType !== "openEnded" && (
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => (
                  <label
                    key={oi}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3.5 cursor-pointer hover:border-gold-400/40 hover:bg-stone/30 transition-colors has-[:checked]:border-gold-400 has-[:checked]:bg-gold-50/50"
                  >
                    <input
                      type="radio"
                      name={`quiz-${q.id}`}
                      checked={mcAnswers[q.id] === opt}
                      onChange={() => setMcAnswers((s) => ({ ...s, [q.id]: opt }))}
                      className="text-gold-500 focus:ring-gold-400"
                    />
                    <span className="text-ink-950">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {q.questionType === "openEnded" && (
              <div className="mt-3">
                <textarea
                  className={`mt-0 w-full rounded-xl border px-4 py-3.5 text-ink-950 focus:ring-1 focus:ring-gold-400 transition-colors ${
                    openAnswerTooShort ? "border-amber-400 focus:border-amber-500" : "border-gray-200 focus:border-gold-400"
                  }`}
                  rows={5}
                  placeholder={`Type your answer here (minimum ${MIN_OPEN_ENDED_WORDS} words)...`}
                  aria-invalid={openAnswerTooShort}
                  aria-describedby={`oe-word-hint-${q.id}`}
                  value={openAnswers[q.id] || ""}
                  onChange={(e) => setOpenAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                />
                <p id={`oe-word-hint-${q.id}`} className="mt-2 text-sm text-gray-500">
                  <span className={openWordCount >= MIN_OPEN_ENDED_WORDS ? "text-green-700 font-medium" : ""}>
                    {openWordCount} / {MIN_OPEN_ENDED_WORDS} words minimum
                  </span>
                  {openAnswerTooShort && (
                    <span className="block mt-0.5 text-amber-700">
                      Add a bit more detail — short answers cannot be submitted.
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !canSubmit}
        className="mt-8 rounded-lg bg-gold-400 px-5 py-2.5 text-ink-950 font-semibold hover:bg-gold-300 disabled:opacity-50 transition-colors"
      >
        {submitting ? "Submitting..." : "Submit quiz"}
      </button>
    </div>
  );
}
